import React from "react"
import {
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"

export interface Column<T = any> {
  id: string
  label: string
  minWidth?: number
  align?: "right" | "left" | "center"
  format?: (value: any, row: T) => React.ReactNode
  sortable?: boolean
}

export interface TableProps<T = any> {
  columns: Column<T>[]
  rows: T[]
  pagination?: boolean
  page?: number
  rowsPerPage?: number
  onPageChange?: (page: number) => void
  onRowsPerPageChange?: (rowsPerPage: number) => void
  selectable?: boolean
  selectedRows?: T[]
  onSelectRow?: (row: T) => void
  onSelectAll?: (selected: boolean) => void
  sortBy?: string
  sortOrder?: "asc" | "desc"
  onSort?: (columnId: string) => void
  getRowId?: (row: T) => string | number
  className?: string
}

export function Table<T = any>({
  columns,
  rows,
  pagination = false,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange: _onRowsPerPageChange,
  selectable = false,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  sortBy,
  sortOrder,
  onSort,
  getRowId = (row: T) => (row as any).id || JSON.stringify(row),
  className,
}: TableProps<T>) {
  const [internalPage, setInternalPage] = React.useState(page)
  const [internalRowsPerPage] = React.useState(rowsPerPage)

  const handleChangePage = (newPage: number) => {
    setInternalPage(newPage)
    onPageChange?.(newPage)
  }

  const isRowSelected = (row: T) => {
    const rowId = getRowId(row)
    return selectedRows.some((selected) => getRowId(selected) === rowId)
  }

  const handleSelectRow = (row: T) => {
    onSelectRow?.(row)
  }

  const handleSelectAll = (checked: boolean) => {
    onSelectAll?.(checked)
  }

  const handleSort = (columnId: string) => {
    onSort?.(columnId)
  }

  const paginatedRows = pagination
    ? rows.slice(internalPage * internalRowsPerPage, internalPage * internalRowsPerPage + internalRowsPerPage)
    : rows

  const allSelected = rows.length > 0 && rows.every((row) => isRowSelected(row))
  const someSelected = rows.some((row) => isRowSelected(row))

  return (
    <div className={cn("rounded-lg border-2 border-input bg-card shadow-sm overflow-hidden overflow-x-auto", className)}>
      <ShadcnTable>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                />
              </TableHead>
            )}
            {columns.map((column) => (
              <TableHead
                key={column.id}
                className={cn(
                  column.align === "right" && "text-right",
                  column.align === "center" && "text-center",
                  column.minWidth && `min-w-[${column.minWidth}px]`
                )}
              >
                {column.sortable ? (
                  <button
                    onClick={() => handleSort(column.id)}
                    className="flex items-center gap-2 font-semibold transition-colors hover:text-primary"
                  >
                    {column.label}
                    {sortBy === column.id ? (
                      sortOrder === "asc" ? (
                        <ArrowUp className="h-4 w-4" />
                      ) : (
                        <ArrowDown className="h-4 w-4" />
                      )
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </button>
                ) : (
                  column.label
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedRows.map((row) => {
            const selected = isRowSelected(row)
            return (
              <TableRow
                key={String(getRowId(row))}
                data-state={selected ? "selected" : undefined}
                onClick={selectable ? () => handleSelectRow(row) : undefined}
                className={cn(selectable && "cursor-pointer")}
              >
                {selectable && (
                  <TableCell>
                    <Checkbox
                      checked={selected}
                      onCheckedChange={() => handleSelectRow(row)}
                      aria-label="Select row"
                    />
                  </TableCell>
                )}
                {columns.map((column) => {
                  const value = (row as any)[column.id]
                  return (
                    <TableCell
                      key={column.id}
                      className={cn(
                        column.align === "right" && "text-right",
                        column.align === "center" && "text-center"
                      )}
                    >
                      {column.format ? column.format(value, row) : value}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </ShadcnTable>
      {pagination && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t-2 border-muted bg-muted/20 px-4 sm:px-6 py-4">
          <div className="text-sm font-medium text-muted-foreground text-center sm:text-left order-2 sm:order-1">
            Showing <span className="font-semibold text-foreground">{internalPage * internalRowsPerPage + 1}</span> to{" "}
            <span className="font-semibold text-foreground">{Math.min((internalPage + 1) * internalRowsPerPage, rows.length)}</span> of{" "}
            <span className="font-semibold text-foreground">{rows.length}</span> entries
          </div>
          <div className="flex items-center gap-2 sm:gap-3 order-1 sm:order-2">
            <button
              onClick={() => handleChangePage(internalPage - 1)}
              disabled={internalPage === 0}
              className="rounded-lg border-2 border-input bg-background px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-accent/50 hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background"
            >
              Previous
            </button>
            <span className="text-sm font-semibold text-foreground min-w-[100px] text-center">
              Page {internalPage + 1} of {Math.ceil(rows.length / internalRowsPerPage)}
            </span>
            <button
              onClick={() => handleChangePage(internalPage + 1)}
              disabled={internalPage >= Math.ceil(rows.length / internalRowsPerPage) - 1}
              className="rounded-lg border-2 border-input bg-background px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-accent/50 hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Table
